// stahlia-frontend/src/api/warehouse.ts
// API-Layer Lager-Modul. Alle Pfade relativ zur BASE '/api' (client.ts).
import { useMemo } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, apiFetch, apiFetchFull, ApiError, isTransportError, type AckResponse } from './client'
import type { ZaehlweiseKurz } from './zaehlweise'
import { OfflineQueue, newIdempotencyKey } from '@/lib/offline-queue'
import { planeMengenAenderung } from '@/lib/mengen-plan'
import {
  idFuerVorgang, vorgangAbschliessen, vorgangsSchluessel, type Vorgangsspeicher,
} from '@/lib/fillup-anforderung'
import { neueUuid } from '@/lib/uuid'
import { triggerBlobDownload } from '@/lib/download'
import type { BestandZeile } from '@/lib/handregal-status'
import type { KorrekturAuftrag } from '@/lib/handregal-korrektur'
import { useAuth } from './auth'
import { useWarehouseCompanyScope, type WarehouseCompany, type WarehouseCompanyFilter } from '@/lib/warehouse-company'

export interface WarehouseCompanyCandidate {
  worklist_id: number
  original_article: string | null
  stock_item_id: number | null
  stored_status: WarehouseCompany | 'unresolved' | null
  resolution: 'unresolved'
  expected_hash: string
  evidence: Record<string, unknown>
}
export interface WarehouseCandidateDecision {
  stock_item_id: number | null
  company: WarehouseCompany
  original_article: string
  expected_hash: string
  reason: string
}
export function useWarehouseCompanyCandidates(offset: number) {
  const { user, warehouse } = useAuth()
  return useQuery({
    queryKey: ['warehouse', 'company-candidates', user?.id, warehouse.lead, offset],
    queryFn: () => apiFetch<{ total: number; candidates: WarehouseCompanyCandidate[] }>(`/warehouse/company-candidates/?limit=100&offset=${offset}`),
    enabled: warehouse.lead,
    retry: false,
  })
}
export function useClassifyWarehouseCandidate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: WarehouseCandidateDecision }) =>
      apiFetch(`/warehouse/company-candidates/${id}/classify/`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['warehouse'] }),
  })
}

export interface WarehousePlace {
  recaptureReservation: { positionId: number; paletteNo: string; generation: number } | null
  isAvailable: boolean
  id: number; barcode: string; rowCode: string; fieldNo: number; slotNo: number
  levelNo: number; zone: 'fuss' | 'stapler'; placeType: string; origin: string
  isActive: boolean; occupancy: number
}
export interface ReceiptListEntry {
  id: number
  mode: 'normal' | 'erstbetriebnahme'
  orderNo: string | null
  firstInDate: string
  piecesPerCarton: number | null
  areaNote: string | null
  status: string
  escalationDays: number
  createdAt: string
  createdByName: string | null
  item: { merchantSku: string; itemName: string | null; totalQty: number } | null
  // Task 10 (WE-V2 Multi-Positionen): Backend liefert seit Task 4 alle Positionen des
  // Auftrags, nicht nur die erste (`item`). `item` bleibt fuer Abwaertskompatibilitaet.
  items: Array<{
    id: number
    merchantSku: string
    itemName: string | null
    totalQty: number
    cartonsDelivered: number | null
    status: string
    etikettenGesamt: number
    etikettenGebucht: number
  }>
  herkunft: {
    quelle: 'einkauf' | 'leiter'
    bestellung: { id: number; orderNumber: string; lieferant: string | null; status: string } | null
    container: { id: number; label: string | null; containerNo: string | null } | null
  }
  progress: { expected: number; booked: number; missing: number; status: string }
  escalation: { level: 'ok' | 'watch' | 'eskaliert'; daysOpen: number }
}
export interface ReceiptLabel {
  id: number; paletteNo: string | null; sheetNo: number
  // Task 11 (WE-V2 Detail-Seite): V2-Etiketten haben qty=NULL bis zur Erfassung
  // am Regal (Migration 0088) — V1-Etiketten tragen weiterhin einen Wert.
  qty: number | null
  receiptItemId: number | null
  status: 'valid' | 'cancelled' | 'booked'; duplicateCount: number; cancelReason: string | null
  // 2026-10-15: true = nach der Auftragsanlage nachgemeldet (App/Web) — Basis der
  // Kopf-Buttons „(hinzugefügt)". Fehlt bei aelteren Backends -> nicht hinzugefuegt.
  isAdded?: boolean
}
export interface ReceiptDetail {
  receipt: { id: number; mode: 'normal' | 'erstbetriebnahme'; orderNo: string | null; firstInDate: string; status: string; escalationDays: number; createdAt: string; createdByName: string | null }
  herkunft: ReceiptListEntry['herkunft']
  items: Array<{
    id: number; merchantSku: string; itemName: string | null; totalQty: number
    // Task 11: cartonsDelivered=NULL markiert V1-Auftraege (Alt-Bestand) —
    // dient der FE-seitigen V1/V2-Weiche fuer den Etiketten-PDF-Button.
    cartonsDelivered: number | null; status: string
    // OI-130 P2: Ist-Summen der gebuchten Etiketten + offener Mengen-Klaerfall
    // der Position (null = keiner offen). Wire-Casing camelCase wie die uebrigen
    // Detail-Felder (Backend liefert Drizzle-Rows unveraendert).
    capturedQty: number; capturedCartons: number; deviationMessage: string | null
    divertedCartons: number
    packstationSourceQty: number | null; packstationPiecesPerCarton: number | null
    packstationConfirmedCartons: number | null; packstationConfirmedStueck: number | null
    packstationConfirmedAt: string | null; packstationConfirmedBy: number | null
    packstationConfirmedByName: string | null
    // Plan 2026-10-17: Mass (Breite x Laenge) fuer die WE-Etiketten; fehlt bei aelteren Backends.
    abmessung?: string | null
    // Plan 2026-10-20 (B4b): Zaehlweise des Grundartikels, nur Anzeige; fehlt bei aelteren Backends.
    zaehlweise?: ZaehlweiseKurz | null
    cancelReason?: string | null
    cancelledAt?: string | null
    cancelledByName?: string | null
  }>
  labels: ReceiptLabel[]
  bookedInfo: Record<number, {
    placeBarcode: string | null
    bookedBy: string | null
    bookedAt: string | null
    positionId?: number | null
    cartons?: number | null
    piecesPerCarton?: number | null
    restPieces?: number | null
    positionState?: string | null
  }>
  progress: { expected: number; booked: number; missing: number; status: string }
  escalation: { level: string; daysOpen: number }
  events: Array<{ id: number; eventType: string; message: string; status: string; createdAt: string }>
  log: Array<{ id: number; action: string; note: string | null; actorName: string | null; createdAt: string }>
}

export type PlaceSort = 'barcode' | 'zone' | 'ebene' | 'belegung'

export function usePlaces(params: {
  rowCode?: string; free?: 'true' | 'false'; q?: string
  zone?: 'fuss' | 'stapler'; sort?: PlaceSort; dir?: 'asc' | 'desc'
}) {
  const scope = useWarehouseCompanyScope()
  const search = new URLSearchParams()
  if (params.rowCode) search.set('row_code', params.rowCode)
  if (params.free) search.set('free', params.free)
  if (params.q) search.set('q', params.q)
  if (params.zone) search.set('zone', params.zone)
  if (params.sort) search.set('sort', params.sort)
  if (params.dir) search.set('dir', params.dir)
  search.set('limit', '500')
  return useQuery({
    queryKey: ['warehouse', 'places', params, scope.key],
    queryFn: () => api.get<WarehousePlace[]>(`/warehouse/place/?${search.toString()}`),
  })
}

// Reihen-Übersicht (R01…R32 mit Platz-Anzahl) für den Filter-Dropdown der Plätze-Seite.
export function usePlaceRows() {
  return useQuery({
    queryKey: ['warehouse', 'place-rows'],
    queryFn: () => apiFetch<{ rows: Array<{ rowCode: string; count: number }> }>('/warehouse/place/rows/')
      .then((r) => r.rows),
    staleTime: 5 * 60 * 1000,
  })
}

export function useGeneratePlaces() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { row_code: string; fields: number; slots: number; levels: number; preview?: boolean }) =>
      apiFetch<{ preview?: boolean; count?: number; sample?: string[]; created?: number; skipped_existing?: number }>(
        '/warehouse/place/generate/', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (_d, vars) => { if (!vars.preview) void qc.invalidateQueries({ queryKey: ['warehouse', 'places'] }) },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen'),
  })
}

export function useReceipts(status?: string) {
  const scope = useWarehouseCompanyScope()
  // Die Übersicht filtert die bis zu 200 geladenen Aufträge im Browser.
  const qs = status ? `?limit=200&status=${status}` : '?limit=200'
  return useQuery({
    queryKey: ['warehouse', 'receipts', status ?? 'alle', scope.key],
    queryFn: () => api.get<ReceiptListEntry[]>(`/warehouse/receipt/${qs}`),
    refetchInterval: 30_000, // Fortschritt "ab Tag 0 live" (Spec §3.4)
  })
}

export interface ReceiptOrder {
  id: number
  orderNumber: string
  lieferant: string | null
  status: string
  orderDate: string
  positionen: number
  container: Array<{
    id: number
    label: string | null
    containerNo: string | null
    status: string
    warehouseReceiptId: number | null
  }>
}
export function useReceiptOrders(enabled: boolean) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'receipt-orders', scope.key],
    queryFn: () => api.get<ReceiptOrder[]>('/warehouse/receipt/bestellungen/'),
    enabled,
  })
}

export function useReceiptDetail(id: number | null) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'receipt', id, scope.key],
    queryFn: () => apiFetch<ReceiptDetail>(`/warehouse/receipt/${id}/`),
    enabled: id !== null,
    refetchInterval: 30_000,
  })
}

export function useResolveArtikel() {
  return useMutation({
    mutationFn: (q: string) =>
      apiFetch<{ artikel: { stockItemId: number; sku: string; merchantSku: string | null; name: string; drQuantity: number | null; lagereinheit: 'meter' | 'stueck'; breiteM: number | null; rollenVorschlag: 1 | 2 | 5 | 10 | 30 | null } }>(
        `/warehouse/artikel/resolve/?q=${encodeURIComponent(q)}`),
  })
}

export function useConfirmWePackstation(receiptId: number, receiptItemId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { cartons: number }) =>
      apiFetch<{ confirmed: boolean; cartons: number; stueck: number; duplicate: boolean }>(
        `/warehouse/receipt/${receiptId}/items/${receiptItemId}/packstation-confirm/`,
        { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse'] })
      void qc.invalidateQueries({ queryKey: ['purchase'] })
    },
  })
}

// Plan 2026-10-16: Leiter setzt die Kartons, die direkt in die Packstation gehen (vor der ersten Palette).
export function useSetWePackstation(receiptId: number, receiptItemId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { cartons: number }) =>
      apiFetch<{ changed: boolean; diverted_cartons: number; total_qty: number; cartons_delivered: number; neues_etikett: string | null }>(
        `/warehouse/receipt/${receiptId}/items/${receiptItemId}/packstation/`,
        { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse'] })
      void qc.invalidateQueries({ queryKey: ['purchase'] })
    },
  })
}

// Plan 2026-10-17: Leiter setzt das Mass der Position fuer die WE-Etiketten (null = entfernen).
export function useSetWeAbmessung(receiptId: number, receiptItemId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { abmessung: string | null }) =>
      apiFetch<{ changed: boolean; abmessung: string | null }>(
        `/warehouse/receipt/${receiptId}/items/${receiptItemId}/abmessung/`,
        { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse'] })
    },
  })
}

// Leere Packstation-Faecher: Nachschub-Standard als Packstation-Kartons fuer den ganzen Auftrag setzen.
export function useWePackstationVorschlag(receiptId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{
        gesetzt: Array<{ receipt_item_id: number; merchant_sku: string; kartons: number }>
        uebersprungen: Array<{ receipt_item_id: number; merchant_sku: string; grund: string }>
      }>(`/warehouse/receipt/${receiptId}/packstation-vorschlag/`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse'] })
      void qc.invalidateQueries({ queryKey: ['purchase'] })
    },
  })
}

// OI-130 P2: Leiter uebernimmt die erfasste Menge als neues Soll — loest den
// Mengen-Klaerfall der Position auf und macht den Auftrag vollstaendig.
export function useAcceptCaptured(receiptId: number) {
  const qc = useQueryClient()
  return useMutation({
    // Plan 2026-10-20 Teil A: Key je Uebernahme-Versuch; „Ja, Stueck korrekt“ sendet denselben Key mit Flag.
    mutationFn: (b: { receiptItemId: number; idempotency_key: string; paare_bestaetigt?: boolean }) =>
      apiFetch<{ item_status: string; qty: number; cartons: number | null; replay?: boolean }>(
        `/warehouse/we/items/${b.receiptItemId}/accept-captured/`, {
          method: 'POST',
          body: JSON.stringify({ idempotency_key: b.idempotency_key, ...(b.paare_bestaetigt ? { paare_bestaetigt: true } : {}) }),
        }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse', 'receipt', receiptId] })
      void qc.invalidateQueries({ queryKey: ['warehouse', 'receipts'] })
      void qc.invalidateQueries({ queryKey: ['warehouse', 'events'] })
    },
  })
}

// Task 10 (WE-V2): /warehouse/receipt/ nimmt jetzt eine Positions-Liste statt
// eines einzelnen Artikels + Paletten-Verteilung. Response liefert pro Position
// direkt das erste Etikett (Sammel-Palette 1) fuer die Auto-PDF (Task 5/10 §5).
export interface CreateReceiptBody {
  items: Array<{ artikel: string; total_qty: number; cartons_delivered: number; abmessung?: string; first_in_date?: string }>
  order_no?: string
  first_in_date?: string
  idempotency_key: string
}
export interface CreateReceiptResponse {
  receipt_id: number
  items: Array<{ receipt_item_id: number; merchant_sku: string; item_name: string | null; label_id: number; palette_no: string }>
}
export function useCreateReceipt() {
  const qc = useQueryClient()
  return useMutation({
    // RA-BI (Re-Audit Runde 1, Welle W2-BE): idempotency_key ist auf
    // /receipt/ jetzt Pflicht (receiptCreateSchema). ReceiptsPage.tsx haelt
    // einen stabilen Key ueber die gesamte Formular-Eingabe (Retry-Schutz);
    // der Fallback hier ist Defense-in-Depth fuer andere/kuenftige Aufrufer.
    mutationFn: (body: CreateReceiptBody) =>
      apiFetch<CreateReceiptResponse>(
        '/warehouse/receipt/', {
          method: 'POST',
          body: JSON.stringify({ ...body, idempotency_key: body.idempotency_key ?? newIdempotencyKey() }),
        }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['warehouse', 'receipts'] }),
  })
}

export function duplicateWarehouseLabel(labelId: number, idempotencyKey?: string) {
  return apiFetch(`/warehouse/label/${labelId}/duplicate/`, { method: 'POST', body: JSON.stringify(idempotencyKey ? { idempotency_key: idempotencyKey } : {}) })
}

export function useReceiptAction(receiptId: number) {
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['warehouse', 'receipt', receiptId] })
    void qc.invalidateQueries({ queryKey: ['warehouse', 'receipts'] })
    void qc.invalidateQueries({ queryKey: ['warehouse', 'artikel-pos'] })
    void qc.invalidateQueries({ queryKey: ['warehouse', 'handregal'] })
    void qc.invalidateQueries({ queryKey: ['purchase'] })
  }
  return {
    cancelLabels: useMutation({
      mutationFn: (b: { label_ids: number[]; reason: string }) =>
        apiFetch(`/warehouse/receipt/${receiptId}/labels/cancel/`, { method: 'POST', body: JSON.stringify(b) }),
      onSuccess: invalidate,
    }),
    regenerate: useMutation({
      // RA-BI (Welle W2-BE): idempotency_key ist auf /labels/regenerate/ jetzt
      // Pflicht (labelsRegenerateSchema). ReceiptDetailPage.tsx haelt einen
      // stabilen Key je Nachdruck-Versuch; Fallback hier ist Defense-in-Depth.
      mutationFn: (b: { receipt_item_id: number; groups: Array<{ count: number; qty: number }>; idempotency_key?: string }) =>
        apiFetch(`/warehouse/receipt/${receiptId}/labels/regenerate/`, {
          method: 'POST',
          body: JSON.stringify({ ...b, idempotency_key: b.idempotency_key ?? newIdempotencyKey() }),
        }),
      onSuccess: invalidate,
    }),
    duplicate: useMutation({
      mutationFn: (labelId: number) => duplicateWarehouseLabel(labelId),
      onSuccess: invalidate,
    }),
    underdelivery: useMutation({
      mutationFn: (b: { reason: string; qty_missing?: number; note?: string }) =>
        apiFetch(`/warehouse/receipt/${receiptId}/underdelivery/`, { method: 'POST', body: JSON.stringify(b) }),
      onSuccess: invalidate,
    }),
    close: useMutation({
      mutationFn: (note?: string) =>
        apiFetch(`/warehouse/receipt/${receiptId}/close/`, { method: 'POST', body: JSON.stringify({ note }) }),
      onSuccess: invalidate,
    }),
    cancelItem: useMutation({
      mutationFn: (b: { itemId: number; reason: string; idempotency_key?: string }) =>
        apiFetch(`/warehouse/receipt/${receiptId}/items/${b.itemId}/cancel/`, {
          method: 'POST',
          body: JSON.stringify({ reason: b.reason, idempotency_key: b.idempotency_key ?? newIdempotencyKey() }),
        }),
      onSuccess: invalidate,
    }),
  }
}

export function useCorrectPositionQty() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: {
      positionId: number
      cartons?: number
      pieces_per_carton?: number
      rest_pieces?: number
      qty?: number
      reason: string
      idempotency_key?: string
      paare_bestaetigt?: boolean
    }) => {
      const { positionId, ...body } = b
      return apiFetch(`/warehouse/position/${positionId}/correct/`, {
        method: 'POST',
        body: JSON.stringify({ ...body, idempotency_key: body.idempotency_key ?? newIdempotencyKey() }),
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse'] })
    },
  })
}

// Task 11 (WE-V2 Detail-Seite): "Weitere Etiketten" je Position — meldet eine
// Ziel-Palettenzahl, Backend legt fehlende Etiketten an (Task 7,
// /we/items/:id/paletten/) und stoesst den OneDrive-Upload-Hinweis an.
export interface WeReportPalettesResponse {
  created: number; duplicate: boolean
  labels: Array<{ label_id: number; palette_no: string }>
  upload_sent: boolean
  onedrive_path?: string | null
}
export function useWeReportPalettes(receiptItemId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: { target_count: number; idempotency_key: string }) =>
      apiFetch<WeReportPalettesResponse>(`/warehouse/we/items/${receiptItemId}/paletten/`, {
        method: 'POST', body: JSON.stringify(b),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse', 'receipt'] })
      void qc.invalidateQueries({ queryKey: ['warehouse', 'receipts'] })
    },
  })
}

/** Binaer-Download (PDF/CSV) mit Auth-Header — client.ts kann nur JSON.
 *  method='GET' fuer reine Export-Endpoints (kein Body, z.B. Inventur-CSV/PDF),
 *  method='POST' (Default) fuer bestehende Aufrufer mit optionalem Body.
 *  C-Minor (Welle W): bei Fehlschlag den ACK-Fehlertext lesen statt nur den
 *  HTTP-Status zu melden — Backend liefert bei nack() ein JSON-Envelope
 *  ({ ack: 0, error: [{ message }] }) auch fuer eigentlich binaere Endpoints. */
export async function downloadPdf(
  path: string, filename: string, body?: unknown, method: 'GET' | 'POST' = 'POST',
): Promise<void> {
  const token = localStorage.getItem('stahlia_token')
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(method === 'POST' ? { body: body != null ? JSON.stringify(body) : JSON.stringify({}) } : {}),
  })
  if (!res.ok) {
    let message = `Download fehlgeschlagen (HTTP ${res.status})`
    try {
      const errBody = (await res.json()) as { error?: Array<{ message?: string }> }
      const first = errBody?.error?.[0]?.message
      if (first) message = first
    } catch { /* kein JSON-Body (echte Binaerdatei o.ae.) — Standardmeldung bleibt */ }
    throw new Error(message)
  }
  const blob = await res.blob()
  triggerBlobDownload(blob, filename)
}

// ---------- Scan-Kette / Buchungen / Journal / Leiter-Listen (Task 16, Spec K2/K3/H1) ----------

export interface ScanInfo {
  kind: 'palette' | 'place' | 'unknown'
  label?: { id: number; paletteNo: string; sheetNo: number; qty: number; status: string; duplicateCount: number }
  position?: { id: number; state: string; qtyExact: number | null; qtyCirca: number | null; firstInAt: string | null; isMisch: boolean } | null
  place?: { barcode: string; zone: string } | Record<string, unknown> | null
  positions?: Array<Record<string, unknown>>
  artikel?: { merchantSku: string; itemName: string | null } | null
  dialog?: { options: string[]; alreadyUnpacked: boolean } | null
  raw?: string
  hint?: string
}
export function useScanInfo() {
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ info: ScanInfo }>(`/warehouse/scan/?code=${encodeURIComponent(code)}`).then((r) => r.info),
  })
}

export type MoveResult = {
  blocked?: boolean; duplicate?: boolean; code?: string; message?: string
  result?: string; occupant_count?: number; qty_circa?: number | null
  hint_mehrfach_offen?: boolean; state?: string; place_barcode?: string
  // C-C1 (Welle W, Pendant B-C1): pickup-cancel meldet, ob die Palette eine frische
  // Aufnahme war (Etikett wieder 'erwartet') oder eine bereits platzierte Palette
  // (Position bleibt 'in_transit' — nur der Aufnehmer wird freigegeben).
  was_fresh_intake?: boolean
}
export const moveQueue = new OfflineQueue('moves')

// ---------- C-I5 (Welle W): Dead-Letter fuer endgueltig abgelehnte gepufferte Buchungen ----------
// flushMoves entfernt blocked/4xx-Items final aus der Queue (siehe Kommentar unten) — ohne
// diese Ablage verschwinden sie sonst spurlos. Persistiert in localStorage (gleiches Muster
// wie OfflineQueue), damit ein Reload die zuletzt abgelehnten Buchungen nicht verwirft.
export interface MoveRejection {
  path: string; paletteNo?: string; code?: string; message?: string; at: string
}
const REJECTIONS_KEY = 'stahlia_whq_rejections'
const REJECTIONS_MAX = 50

function pushRejections(items: MoveRejection[]): void {
  if (items.length === 0) return
  try {
    const existing = getRejections()
    localStorage.setItem(REJECTIONS_KEY, JSON.stringify([...existing, ...items].slice(-REJECTIONS_MAX)))
  } catch { /* localStorage nicht verfuegbar — Rejection-Anzeige ist Zusatzinfo, kein Fail */ }
}
export function getRejections(): MoveRejection[] {
  try { return JSON.parse(localStorage.getItem(REJECTIONS_KEY) ?? '[]') as MoveRejection[] }
  catch { return [] }
}
export function clearRejections(): void {
  try { localStorage.removeItem(REJECTIONS_KEY) } catch { /* ignore */ }
}

export function deviceLabel(): string {
  let d = localStorage.getItem('stahlia_device_label')
  if (!d) {
    d = window.prompt('Geraete-Name fuer das Journal (z.B. "PDA-1", "Telefon Ali")?') ?? 'unbenannt'
    localStorage.setItem('stahlia_device_label', d)
  }
  return d
}

// RA-WC1: pickup-cancel darf NICHT wie eine normale Buchung offline gepuffert werden —
// die Antwort traegt was_fresh_intake (Palette bleibt in_transit vs. wirklich abgebrochen)
// und der Aufrufer MUSS das live auswerten, um die Scan-Phase korrekt zu setzen. Ein
// gepuffertes 'queued' wuerde den Rueckstellplatz-Hinweis verlieren und der Nutzer landet
// stillschweigend am Start, waehrend die Palette serverseitig weiter 'in_transit' haengt.
// Deshalb ist Abbrechen bewusst online-only (Minimal-Fix): bei Transportfehlern wird NICHT
// gepuffert, sondern ein klarer Fehler geworfen, den der Aufrufer als "braucht Verbindung"
// anzeigt — die Palette bleibt unveraendert im aktuellen Bewegungs-Schritt stehen.
export class MoveOfflineUnavailableError extends Error {
  constructor() { super('Aktion braucht eine Verbindung — bitte online erneut versuchen') }
}

/** Buchung mit Offline-Pufferung: bei Netzfehler → Queue + NICHT-GEBUCHT-Status.
 *  opts.allowOffline=false (RA-WC1, z.B. pickup-cancel) wirft stattdessen
 *  MoveOfflineUnavailableError statt zu puffern. */
export async function sendMove(
  path: string, body: Record<string, unknown>, opts: { allowOffline?: boolean } = {},
): Promise<MoveResult | { queued: true }> {
  const payload = { ...body, idempotency_key: (body['idempotency_key'] as string) ?? newIdempotencyKey(), device_label: deviceLabel() }
  try {
    return await apiFetch<MoveResult>(path, { method: 'POST', body: JSON.stringify(payload) })
  } catch (err) {
    // Transportfehler puffern (Netzfehler = TypeError von fetch, 401 nach Session-Klemmer,
    // 5xx) — gleiche Klassifizierung wie flushMoves, jetzt als geteilter Helfer
    // isTransportError() (RA3-WEB-B, client.ts). Fachliche Server-Ablehnungen
    // (harte 4xx) weiter durchreichen.
    if (isTransportError(err)) {
      if (opts.allowOffline === false) {
        throw new MoveOfflineUnavailableError()
      }
      moveQueue.enqueue({ path, body: payload })
      return { queued: true }
    }
    throw err
  }
}

/** Puffer-Nachversand: 401/5xx duerfen die Queue NICHT verzehren (Session-/Server-Fehler sind
 *  vorruebergehend — nach Re-Login/Recovery soll das Item erneut versucht werden). Nur ein harter
 *  4xx-Reject (z.B. 400/404/409) ist final und wird entfernt, aber ueber `rejected` gemeldet.
 *  ACHTUNG: fachliche Ablehnungen (K2, Storno, Warnung) kommen als HTTP 200 mit `blocked: true` —
 *  auch die sind final und MUESSEN als `rejected` gemeldet werden, sonst verschwindet die
 *  Buchung still aus der Queue und der Mitarbeiter erfaehrt nichts. */
export async function flushMoves(): Promise<{ sent: number; failed: number; rejected: number; rejections: MoveRejection[] }> {
  let rejected = 0
  const rejections: MoveRejection[] = []
  const res = await moveQueue.flush(async (item) => {
    const paletteNo = typeof item.body['palette_no'] === 'string' ? item.body['palette_no'] as string : undefined
    try {
      const r = await apiFetch<MoveResult>(item.path, { method: 'POST', body: JSON.stringify(item.body) })
      if (r?.blocked) { // fachlich abgelehnt: aus Queue entfernen, aber MELDEN (C-I5: mit Details)
        rejected++
        rejections.push({ path: item.path, paletteNo, code: r.code, message: r.message, at: new Date().toISOString() })
      }
      return true
    }
    catch (err) {
      if (err instanceof TypeError) return false                       // Netzfehler → behalten
      if (err instanceof ApiError) {
        if (err.status === 401 || err.status >= 500) return false      // Session/Server → behalten (Re-Login sendet nach)
        rejected++                                                     // harter 4xx-Reject → entfernen, aber MELDEN
        rejections.push({ path: item.path, paletteNo, code: err.code, message: err.message, at: new Date().toISOString() })
        return true
      }
      return false
    }
  })
  pushRejections(rejections)
  // sent = wirklich gebucht; blocked-Items stecken in res.sent (Queue-Entfernung), zaehlen aber als rejected
  return { sent: res.sent - rejected, failed: res.failed, rejected, rejections }
}

export function useJournal(f: Record<string, string>, company?: WarehouseCompanyFilter) {
  const scope = useWarehouseCompanyScope(company)
  const qs = new URLSearchParams(f).toString()
  const { offset: _offset, ...filterOhneSeite } = f
  const filterKey = JSON.stringify([filterOhneSeite, scope.key])
  return useQuery({
    queryKey: ['warehouse', 'journal', f, scope.key],
    queryFn: () => api.get<Array<Record<string, unknown>>>(`/warehouse/journal/?${qs}${company && company !== 'both' && scope.query ? `&${scope.query}` : ''}`),
    retry: false,
    // Beim Blaettern (nur offset anders) bleibt die alte Seite stehen, bis die neue da ist —
    // sonst klappt die Tabelle zusammen und der Klick wirkt, als sei nichts passiert.
    placeholderData: (prev, prevQuery) => {
      if (!prevQuery) return undefined
      const { offset: _o, ...prevFilter } = prevQuery.queryKey[2] as Record<string, string>
      return JSON.stringify([prevFilter, prevQuery.queryKey[3]]) === filterKey ? prev : undefined
    },
  })
}

/** Auswahlwerte der Journal-Filter (Aktion / Wer / Geraet). */
export function useJournalFacets() {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'journal', 'facets', scope.key],
    queryFn: () => apiFetch<{ actions: string[]; actors: string[]; devices: string[] }>('/warehouse/journal/facets/'),
    staleTime: 5 * 60_000,
    retry: false,
  })
}
// Codex F3 (OI-130): echte Gesamt-Zaehler fuer die Leiter-Listen-Reiter — die
// Listen-Endpoints sind auf 100 gedeckelt, ihre Laenge taugt nicht als Zaehler.
// Response hat kein `data`-Feld -> apiFetch liefert das Objekt ungeschaelt.
// `company` (Leiter-Listen): Leiter sehen ohne Angabe beide Firmen; mit Angabe filtert der
// Server per ?company=. Ohne Argument bleiben alle bisherigen Aufrufer unveraendert.
const companySuffix = (scope: { query: string }, company: WarehouseCompany | undefined, sep: '?' | '&') =>
  company && scope.query ? `${sep}${scope.query}` : ''

export function useOpenCounts(company?: WarehouseCompany) {
  const scope = useWarehouseCompanyScope(company)
  return useQuery({
    queryKey: ['warehouse', 'events', 'counts', scope.key],
    queryFn: () => apiFetch<{ events: Record<string, number>; quarantine_open: number }>(`/warehouse/events/counts/${companySuffix(scope, company, '?')}`),
    refetchInterval: 30_000,
  })
}

export function useEvents(status: 'open' | 'resolved', type?: string, company?: WarehouseCompany, limit?: number) {
  const scope = useWarehouseCompanyScope(company)
  const qs = new URLSearchParams({ status, ...(type ? { type } : {}), ...(limit ? { limit: String(limit) } : {}) }).toString()
  return useQuery({
    queryKey: ['warehouse', 'events', status, type ?? '', limit ?? null, scope.key],
    queryFn: () => api.get<Array<Record<string, unknown>>>(`/warehouse/events/?${qs}${companySuffix(scope, company, '&')}`),
    refetchInterval: 30_000,
  })
}
export function useResolveEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: { id: number; note: string }) =>
      apiFetch(`/warehouse/events/${a.id}/resolve/`, { method: 'POST', body: JSON.stringify({ note: a.note }) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['warehouse', 'events'] }),
  })
}
export function useTransit(all: boolean, company?: WarehouseCompany) {
  const scope = useWarehouseCompanyScope(company)
  return useQuery({
    queryKey: ['warehouse', 'transit', all, scope.key],
    queryFn: () => api.get<Array<Record<string, unknown>>>(`/warehouse/transit/?all=${all}${companySuffix(scope, company, '&')}`),
    refetchInterval: 15_000,
  })
}
export interface ArtikelPosition {
  id: number
  labelId: number | null
  paletteNo: string | null
  placeId: number | null
  placeBarcode: string | null
  state: 'in_transit' | 'verschlossen' | 'ausgepackt' | 'ausgelagert' | 'leer'
  qtyExact: number | null
  qtyCirca: number | null
  cartons: number | null
  piecesPerCarton: number | null
  // angebrochener Rest-Karton beim WE in Stück (Menge = Kartons × Stück/Karton + Rest)
  restPieces?: number | null
  netzeRollLengthM: number | null
  netzeRollsPerCarton: number | null
  netzeUnitsVersion: 'netze-m-v1' | null
  firstInAt: string | null
  isMisch: boolean
  // Vorrang-Palette des Leiters (Plan 2026-10-18); fehlt bei älterem Backend
  vorrang?: { modus: 'sofort' | 'naechste'; setByName: string | null; setAt: string } | null
  // Packplatz des Artikels auf diesem Platz (Plan 2026-10-22).
  placeType?: string | null
  packplatz?: { setByName: string | null; setAt: string } | null
}
export interface ArtikelPositionen {
  artikel: { stockItemId: number; sku: string; merchantSku: string | null; name: string; drQuantity: number } | null
  positions: ArtikelPosition[]
  // kartonSum: Teil von circaSum, den der Pick kartongenau fortschreibt (fehlt bei älterem Backend).
  summary: { exactSum: number; circaSum: number; unknownCount: number; total: number; isCirca: boolean; kartonSum?: number;
    drQuantity: number | null; unit: 'm' | 'St.';
    lager1: number; lager2: number
    packstation: { physisch: number; frei: number; aktuell: boolean; countedAt: string } | null
    packstationHinweis: 'noch_nicht_gezaehlt' | 'zaehler_nicht_aktuell' | null
    gesamt: number; gesamtUnvollstaendig: boolean }
}
export interface UnlinkedPosition {
  id: number
  labelId: number | null
  placeId: number | null
  paletteNo: string | null
  placeBarcode: string | null
  sourceArticle: string | null
  freeText: string | null
  rawCode: string | null
  createdAt: string
}
export function useArtikelPositionen(merchantSku: string | null) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'artikel-pos', merchantSku, scope.key],
    queryFn: () => apiFetch<ArtikelPositionen>(`/warehouse/artikel/${encodeURIComponent(merchantSku!)}/positions/`),
    enabled: !!merchantSku,
  })
}
export function useUnlinked(company?: WarehouseCompany) {
  const scope = useWarehouseCompanyScope(company)
  return useQuery({
    queryKey: ['warehouse', 'unlinked', scope.key],
    queryFn: () => api.get<UnlinkedPosition[]>(`/warehouse/unlinked/${companySuffix(scope, company, '?')}`),
  })
}

// ---------- Quarantäne-Bergung (Task 6, RES2-Web, Backend Migration 0087) ----------
// App laedt fail-closed quarantaenisierte Blobs hoch (Outbox-Eintrag mit invalidem
// Continuation-Meta, WAL-/Cancel-Reste); das Leiter-Board zeigt sie inkl. JSON-Payload.
// Lesen = 'general' (Backend requireWarehouse('general')), Aufloesen = 'lead'.
//
// A3 (Codex-Fix, MEDIUM, 2026-07-16): die Liste liefert seit Migration der
// Backend-Route keine `payload`-Spalte mehr (30s-Polling + potenziell 64 kB
// Payload je Eintrag waere sonst ein unnoetig grosser Wiederhol-Response). Der
// volle Report inkl. `payload` kommt jetzt ueber `/warehouse/quarantine/:id/`,
// lazy nachgeladen (Aufklappen ODER Download).
// C1 (Codex-Fix2, MEDIUM, 2026-07-16): Seite mit Cursor-Fortsetzung — die
// Backend-Antwort traegt jetzt `next_before_id` als Envelope-Sibling neben
// `data` (nicht unter `pagination`, siehe routes.ts) — `api.get` wuerde das
// wegwerfen (unwrapt nur `body.data`), deshalb hier `apiFetchFull`.
// E1 (Codex-Fix3, MEDIUM, 2026-07-16): der C1-Hand-State (`extraRows` im
// Page-Component, Seite 1 gepollt/Folgeseiten statisch) wird stale, sobald
// Resolve oder neue Reports die Seitengrenzen verschieben — ein auf einer
// Zusatzseite aufgeloester Eintrag blieb dann bis zum naechsten manuellen
// Reload unter "Offen" sichtbar. Fix: kanonisches TanStack-Muster
// `useInfiniteQuery` — Status ist Teil des Query-Keys, `getNextPageParam`
// liefert den naechsten Cursor, und `invalidateQueries({queryKey:
// ['warehouse','quarantine']})` (siehe useResolveQuarantineReport unten)
// refetcht ALLE bereits geladenen Seiten statt nur Seite 1.
export interface QuarantinePage {
  items: Array<Record<string, unknown>>
  nextBeforeId: number | null
}
export function useQuarantineReports(status: 'open' | 'resolved', company?: WarehouseCompany) {
  const scope = useWarehouseCompanyScope(company)
  return useInfiniteQuery({
    queryKey: ['warehouse', 'quarantine', status, scope.key],
    queryFn: async ({ pageParam }): Promise<QuarantinePage> => {
      const suffix = pageParam != null ? `&before_id=${pageParam}` : ''
      const res = await apiFetchFull<Array<Record<string, unknown>>>(`/warehouse/quarantine/?status=${status}${suffix}${companySuffix(scope, company, '&')}`)
      return {
        items: res.data ?? [],
        nextBeforeId: (res as unknown as { next_before_id?: number | null }).next_before_id ?? null,
      }
    },
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextBeforeId,
    refetchInterval: 30_000,
  })
}
/** Lazy: nur aktiv, wenn `enabled` (Aufklappen) — vermeidet einen Detail-Fetch
 *  pro Zeile bei jedem 30s-Poll der Liste. */
export function useQuarantineReportDetail(id: number, enabled: boolean) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'quarantine', 'detail', id, scope.key],
    queryFn: () => api.get<Record<string, unknown>>(`/warehouse/quarantine/${id}/`),
    enabled,
  })
}
/** Fuer den Download-Button: onClick-Fetch statt Hook, damit "Download" auch
 *  funktioniert, ohne vorher "Payload anzeigen" geklickt zu haben. */
export function fetchQuarantineReportDetail(id: number): Promise<Record<string, unknown>> {
  return api.get<Record<string, unknown>>(`/warehouse/quarantine/${id}/`)
}
export function useResolveQuarantineReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: { id: number; resolution_note?: string }) =>
      apiFetch(`/warehouse/quarantine/${a.id}/resolve/`, {
        method: 'POST',
        body: JSON.stringify({ resolution_note: a.resolution_note || undefined }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['warehouse', 'quarantine'] }),
  })
}

// ---------- Lager-Mitarbeiter (Scan-App OI-076) ----------
// A-I11a (Welle S2): pin_salt/pin_hash kommen nur noch fuer Geraete (api_client-
// Sync) mit — das Web-FE nutzt sie nirgends, deshalb optional statt required.
export interface WarehouseWorker {
  id: number; name: string; pin_salt?: string; pin_hash?: string
  is_active: boolean; can_inventur: boolean; can_wareneingang: boolean
  can_nachschub: boolean; can_pick: boolean
  // Modul 23 A (Migration 0113): Rechte-Modell 1:1 — jede Kachel der Scan-App ein
  // eigenes Haekchen, dazu die beiden Listen-Rechte unter „Kommissionieren".
  // Optional, damit die Seite auch gegen ein Backend vor 0113 noch rendert (dort
  // fehlen die Felder schlicht); die Anzeige liest sie ueber `=== true`.
  can_einlagern?: boolean; can_umfahren?: boolean; can_info?: boolean; can_buchungen?: boolean
  can_erstaufnahme?: boolean; can_handregal?: boolean; can_leerplatz_pruefen?: boolean
  can_liste_fuss?: boolean; can_liste_stapler?: boolean
  can_priostahl_erstaufnahme?: boolean; can_priostahl_nachschub?: boolean; can_priostahl_pick?: boolean
  can_priostahl_umfahren?: boolean
  can_packplatz_setzen?: boolean
  // 0172: NULL-MENGEN am Packort zählen.
  can_nullmengen?: boolean
}
export function useWorkers() {
  return useQuery({
    queryKey: ['warehouse', 'workers'],
    queryFn: () => apiFetch<{ workers: WarehouseWorker[]; device_client_id: number | null }>('/warehouse/workers/')
      .then((r) => r.workers),
    retry: false,
  })
}
export function useSaveWorker() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: { id?: number; body: Record<string, unknown> }) =>
      apiFetch<{ conflict?: boolean; message?: string; id?: number }>(
        a.id ? `/warehouse/workers/${a.id}/` : '/warehouse/workers/',
        { method: 'POST', body: JSON.stringify(a.body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['warehouse', 'workers'] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen'),
  })
}
// OI-130 P1: endgueltiges Loeschen (lead) — Journal behaelt den Namen (denormalisiert).
export function useDeleteWorker() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ deleted: boolean }>(`/warehouse/workers/${id}/delete/`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['warehouse', 'workers'] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Loeschen fehlgeschlagen'),
  })
}

// ---------- Nachschub / Fillup-Dashboard (Task 14, Task-8-Backend Modul ②) ----------
// Wire-Casing VERIFIZIERT gegen src/modules/warehouse/routes.ts (mapPickListEntry +
// GET /fillup/overview/ + GET /fillup/overrides/ + POST /fillup/request/:id/cancel/,
// Stand Task 8) — snake_case, NICHT camelCase (Lehre T6: Backend-Code lesen, nicht raten).
// ACHTUNG Pfad-Prefix: warehouseRoutes ist in app.ts mit { prefix: '/warehouse' }
// registriert — Fillup-Pfade heissen auf dem Draht /warehouse/fillup/... (404-Bugfix
// 2026-07-17: Prefix fehlte hier, Test spiegelte den falschen Pfad).
// Picking-Positionen bleiben lose typisiert (Record<string, unknown>, Bracket-Zugriff)
// analog zum Muster in useEvents/useTransit/useUnlinked/useQuarantineReports oben.
export interface FillupOverview {
  lists: { fuss: Array<Record<string, unknown>>; stapler: Array<Record<string, unknown>> }
  vormerkungen: Array<Record<string, unknown>>
  progress: { offen: number; erledigt_heute: number }
}
export function useFillupOverview(company?: WarehouseCompanyFilter, enabled = true) {
  const scope = useWarehouseCompanyScope(company)
  return useQuery({
    queryKey: ['warehouse', 'fillup', 'overview', scope.key],
    queryFn: () => apiFetch<FillupOverview>(`/warehouse/fillup/overview/${company && company !== 'both' && scope.query ? `?${scope.query}` : ''}`),
    retry: false,
    enabled,
    refetchInterval: 30_000, // gleiche Live-Kadenz wie useReceipts/useEvents
  })
}

/** Leiter-Storno EINER Anforderung per DB-id (request_db_id aus der Picking-Liste) —
 *  Backend-Gate 'lead' (POST /fillup/request/:id/cancel/, routes.ts). Idempotent:
 *  bereits geschlossene Anforderungen liefern already_closed:true statt Fehler. */
export function useCancelFillupRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (requestDbId: number) =>
      apiFetch<{ cancelled: boolean; already_closed?: boolean }>(
        `/warehouse/fillup/request/${requestDbId}/cancel/`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['warehouse', 'fillup'] }),
  })
}

// ---------- Modul 25: manuelle Anforderung im Web ----------
// Schreibpfad + Vorschau existieren seit Modul ② und werden von der Scan-App
// taeglich benutzt — das Web bekommt hier nur die fehlenden Hooks.
// Wire-Casing gegen src/modules/warehouse/routes.ts Z.1288/1328 verifiziert.

/** Antwort von GET /warehouse/fillup/request/preview/ (routes.ts Z.1335). */
export interface FillupVorschau {
  request_kind?: 'cartons' | 'pallet'
  source_lager?: 1 | 2 | null
  target_lager?: 1 | 2
  merchant_sku: string
  artikel_name: string | null
  open_request: { id: number; cartons: number; status: string } | null
  has_stock: boolean
}

/** Kuerzeste Nummer, die das Backend-Schema akzeptiert (`code: z.string().min(2)`) —
 *  darunter wird gar nicht erst gefragt. */
export const FILLUP_CODE_MIN_LAENGE = 2
/** Laengste Nummer, die das Backend-Schema akzeptiert (`code: z.string().max(40)`).
 *  `merchant_sku` ist in der DB ein unbegrenztes `text` — die Grenze steht also nur
 *  im Schema und muss vorne selbst geprueft werden, sonst gibt es ein nacktes 400. */
export const FILLUP_CODE_MAX_LAENGE = 40

export function useFillupVorschau(code: string, company?: WarehouseCompanyFilter) {
  const scope = useWarehouseCompanyScope(company)
  const bereinigt = code.trim()
  return useQuery({
    queryKey: ['warehouse', 'fillup', 'vorschau', bereinigt, scope.key],
    queryFn: () => apiFetch<FillupVorschau>(
      `/warehouse/fillup/request/preview/?code=${encodeURIComponent(bereinigt)}${company && company !== 'both' ? `&company=${company}` : ''}`),
    enabled: bereinigt.length >= FILLUP_CODE_MIN_LAENGE,
    // Eine unbekannte Nummer ist ein 404 — Wiederholen bringt nichts und wuerde
    // die Rueckmeldung an den Bediener nur verzoegern.
    retry: false,
    staleTime: 15_000,
  })
}

/** Die drei Antwortformen von POST /warehouse/fillup/request/ (routes.ts Z.1297–1325):
 *  fachliche Ablehnung (ACK mit 200, KEIN HTTP-Fehler), blanker Wiederholungs-Treffer
 *  ohne Detaildaten, oder die volle Antwort. */
export type FillupAnforderungErgebnis =
  | { blocked: true; code: string; message: string }
  | { duplicate: true }
  | {
      request_db_id: number
      status: string
      duplicate: boolean
      vorgemerkt: boolean
      cartons_requested?: number
      updated?: boolean
      pick_item_id?: number
      request_kind?: 'pallet'
      pallets?: number
      cartons?: null
      source_lager?: 1 | 2 | null
      target_lager?: 1 | 2
    }

export interface FillupAnforderungEingabe {
  code: string
  cartons: number
  request_id: string
  company?: 'stahlia' | 'priostahl'
}

/** Legt eine Nachschub-Anforderung an. `mode` ist hier fest `'add'`: eine bereits
 *  offene Anforderung wird ERHOEHT, nie ueberschrieben — `'set'` wuerde die von
 *  jemand anderem angeforderte Menge still ersetzen und wird im Web nicht angeboten.
 *  Der Body geht als OBJEKT an api.post, das serialisiert selbst (client.ts Z.203);
 *  ein vorab serialisierter String kaeme im Backend als "Expected object, received
 *  string" an (Fehlerklasse Hotfix 23.1). */
export function useCreateFillupRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (eingabe: FillupAnforderungEingabe) =>
      api.post<FillupAnforderungErgebnis>('/warehouse/fillup/request/', { ...eingabe, mode: 'add' }),
    onSuccess: (ergebnis) => {
      // Eine fachliche Ablehnung hat nichts geschrieben — dann auch nichts neu laden.
      if ('blocked' in ergebnis) return
      void qc.invalidateQueries({ queryKey: ['warehouse', 'fillup'] })
      // Ein Palettenauftrag steht auf derselben Seite in der Palettenliste (eigener Schluessel).
      void qc.invalidateQueries({ queryKey: ['warehouse', 'pallet'] })
    },
  })
}

/** Modul 26 B: Menge EINER Listenzeile aendern.
 *
 *  Die Zeile zeigt die offene Position, der Endpunkt kennt nur die Gesamtmenge der
 *  Anforderung — `planeMengenAenderung` uebersetzt zwischen beidem. Eine Erhoehung
 *  laeuft als `add` mit dem Delta (exakt, ohne Vorwissen). Eine Senkung braucht die
 *  Gesamtmenge; die holt dieser Hook aus der Vorschau, statt sie zu raten. */
export type ZeilenmengeErgebnis = { unveraendert: true } | FillupAnforderungErgebnis

export function useAendereZeilenmenge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (eingabe: {
      merchantSku: string; alteMenge: number; neueMenge: number
      /** OI-134: die Anforderung, die in DIESER Zeile steht. Sie geht mit auf die
       *  Leitung; der Server lehnt ab, wenn inzwischen eine andere Anforderung derselben
       *  Sternnummer offen ist (Storno + Neuanlage zwischen Anzeige und Klick). */
      requestDbId: number
      /** Idempotenz-Speicher der Zeile. Die Id entsteht ERST, wenn feststeht, was
       *  gesendet wird — sie haengt am Inhalt, nicht an der Absicht (Befund C). */
      vorgaenge: Vorgangsspeicher
    }): Promise<ZeilenmengeErgebnis> => {
      let plan = planeMengenAenderung(eingabe.alteMenge, eingabe.neueMenge, null)
      if (plan.modus === 'unbekannt') {
        // Senkung. Hier BEIDE Zahlen frisch holen: die Gesamtmenge aus der Vorschau
        // und die offene Menge aus der Liste. Eine veraltete Zeilenmenge mit einer
        // frischen Gesamtmenge zu verrechnen ergibt eine falsche Zielmenge
        // (Gemini-Review 2026-08-04, Befund B1).
        const [uebersicht, vorschau] = await Promise.all([
          apiFetch<FillupOverview>('/warehouse/fillup/overview/'),
          apiFetch<FillupVorschau>(
            `/warehouse/fillup/request/preview/?code=${encodeURIComponent(eingabe.merchantSku)}`),
        ])
        const frischeZeile = [...uebersicht.lists.fuss, ...uebersicht.lists.stapler]
          .find((z) => String(z['merchant_sku']) === eingabe.merchantSku)
        if (!frischeZeile) {
          throw new Error('Die Anforderung ist nicht mehr offen — bitte die Liste neu laden.')
        }
        // OI-134: Die frische Zeile wird ueber die STERNNUMMER gefunden — sie kann eine
        // ANDERE Anforderung sein als die angeklickte (Storno + Neuanlage dazwischen).
        // Mit deren Zahlen weiterzurechnen hiesse, eine fremde Anforderung zu stellen.
        // Der Server lehnt das ohnehin ab; hier wird nur frueher und klarer abgebrochen,
        // statt einen Wert zu senden, der aus zwei verschiedenen Anforderungen stammt.
        if (Number(frischeZeile['request_db_id']) !== eingabe.requestDbId) {
          throw new Error('Diese Anforderung wurde inzwischen ersetzt — bitte die Liste neu laden.')
        }
        plan = planeMengenAenderung(
          Number(frischeZeile['cartons']), eingabe.neueMenge, vorschau.open_request?.cartons ?? null)
      }
      if (plan.modus === 'keine') return { unveraendert: true }
      if (plan.modus === 'unbekannt') {
        throw new Error('Die Anforderung ist nicht mehr offen — bitte die Liste neu laden.')
      }
      if (plan.modus === 'zu_gross') {
        throw new Error(
          `Dafür müsste die Anforderung auf ${plan.cartons} Kartons stehen — das Lager nimmt `
          + 'höchstens 999. Bitte die Anforderung stornieren und neu anlegen.')
      }
      // Schluessel = was tatsaechlich auf die Leitung geht. Eine Wiederholung
      // desselben Schreibvorgangs traegt dieselbe Id; ein anderer Vorgang, der
      // zufaellig dieselbe Zielmenge hat, bekommt eine eigene.
      const schluessel = vorgangsSchluessel(eingabe.merchantSku, plan.modus, plan.cartons)
      const requestId = idFuerVorgang(eingabe.vorgaenge, schluessel, neueUuid)
      const ergebnis = await api.post<FillupAnforderungErgebnis>('/warehouse/fillup/request/', {
        code: eingabe.merchantSku,
        cartons: plan.cartons,
        request_id: requestId,
        mode: plan.modus,
        // OI-134: der Server prueft, ob das noch dieselbe Anforderung ist.
        request_db_id: eingabe.requestDbId,
      })
      // Eindeutiger Ausgang -> Vorgang freigeben, damit derselbe Schritt spaeter
      // bewusst wiederholt werden kann. Bei `blocked` wurde nichts geschrieben,
      // der Eintrag darf trotzdem weg (kein Wiederholungs-Anker im Backend).
      vorgangAbschliessen(eingabe.vorgaenge, schluessel)
      return ergebnis
    },
    onSuccess: (ergebnis) => {
      if ('blocked' in ergebnis || 'unveraendert' in ergebnis) return
      void qc.invalidateQueries({ queryKey: ['warehouse', 'fillup'] })
    },
  })
}

// Cursor-Muster EXAKT wie useQuarantineReports (apiFetchFull, weil next_before_id
// als Envelope-Sibling neben `data` liegt, siehe GET /fillup/overrides/ in routes.ts).
export interface FillupOverridePage {
  items: Array<Record<string, unknown>>
  nextBeforeId: number | null
}
export function useFillupOverrides(sinceDays: 7 | 30, company?: WarehouseCompanyFilter) {
  const scope = useWarehouseCompanyScope(company)
  const since = useMemo(
    () => new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000).toISOString(),
    [sinceDays],
  )
  return useInfiniteQuery({
    queryKey: ['warehouse', 'fillup', 'overrides', sinceDays, scope.key],
    queryFn: async ({ pageParam }): Promise<FillupOverridePage> => {
      const suffix = pageParam != null ? `&before_id=${pageParam}` : ''
      const res = await apiFetchFull<Array<Record<string, unknown>>>(
        `/warehouse/fillup/overrides/?since=${encodeURIComponent(since)}${suffix}${company && company !== 'both' && scope.query ? `&${scope.query}` : ''}`)
      return {
        items: res.data ?? [],
        nextBeforeId: (res as unknown as { next_before_id?: number | null }).next_before_id ?? null,
      }
    },
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextBeforeId,
  })
}

// ---------- Sicherung (Warehouse-Backup V1, Task 5) ----------

export function useTriggerBackup() {
  return useMutation({
    mutationFn: async () => { await apiFetch('/warehouse/backup/run/', { method: 'POST' }) },
    onSuccess: () => toast.success('Sicherung angestoßen — Ablage in SharePoint folgt in ~1 Minute'),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Sicherung fehlgeschlagen'),
  })
}

/** Blob-Download ausserhalb des JSON-apiFetch, analog downloadPdf() oben.
 *  Auth = Bearer aus localStorage (Repo-Realitaet: KEINE Cookie-Session,
 *  credentials:'include' existiert nirgends im Repo und funktioniert nicht). */
export async function downloadBackupZip(): Promise<void> {
  const token = localStorage.getItem('stahlia_token')
  const res = await fetch('/api/warehouse/backup/export/', {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) throw new Error(`Export fehlgeschlagen (${res.status})`)
  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') ?? ''
  const filename = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'warehouse_backup.zip'
  triggerBlobDownload(blob, filename)
}

// ------------------------------------------------------------
// Handregal-Karte (Modul 19) — vollstaendiger Bestand ueber alle Cursor-Seiten.
// Der Endpunkt /handregal/ ist auf 1000 Zeilen gedeckelt; die Karte braucht bis zu 1.658.
// ------------------------------------------------------------

const HANDREGAL_SEITE = 1000
const HANDREGAL_MAX_SEITEN = 20   // 20.000 Faecher — weit ueber dem Regal, faengt Endlosschleifen

export interface FachBelegungDto {
  nummer: number
  status: 'belegt' | 'frei' | 'unklar'
  kopf: { id: number; sku: string; merchant_sku: string; name: string | null } | null
  grund: 'verschiedene_grundartikel' | 'verschiedene_einzelartikel' | 'nur_fremde_varianten' | null
  artikel: Array<{ id: number; sku: string; name: string | null; quantity: number | null }>
  artikel_anzahl: number
}
export interface HandregalBelegung {
  stand: string | null
  faecher: Map<number, FachBelegungDto>
}
export function useHandregalBelegung() {
  const scope = useWarehouseCompanyScope()
  return useQuery<HandregalBelegung>({
    queryKey: ['warehouse', 'handregal', 'belegung', scope.key],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const antwort = await apiFetchFull('/warehouse/handregal/belegung/') as
        AckResponse & { stand: string | null; faecher: FachBelegungDto[] }
      return { stand: antwort.stand, faecher: new Map(antwort.faecher.map(f => [f.nummer, f])) }
    },
  })
}

export interface HandregalNachschubWert {
  art: 'stueck' | 'kartons'
  menge: number
}
interface HandregalNachschubAntwort extends HandregalNachschubWert {
  merchant_sku: string
  ist_standard: boolean
}
interface HandregalNachschubEingabe {
  merchantSku: string
  menge_stueck: number | null
  menge_kartons: number | null
}

export function useHandregalNachschub() {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'handregal', 'nachschub', scope.key],
    queryFn: async () => {
      const rows = await apiFetch<Array<HandregalNachschubWert & { merchant_sku: string }>>('/warehouse/handregal/nachschub/')
      return new Map(rows.map(row => [row.merchant_sku, { art: row.art, menge: row.menge }]))
    },
  })
}

export interface HandregalZaehlerStand {
  letzter_erfolg: string | null
  agent_lesbar: boolean
  aktuell: boolean
}
export interface HandregalZaehlstandZeile {
  merchant_sku: string
  gezaehlt: number
  counted_at: string
  abgang: number
  zugang: number
  physisch: number | null
  offen: number | null
  frei: number | null
}
interface HandregalZaehlstandAntwort extends AckResponse<HandregalZaehlstandZeile[]> {
  stand: HandregalZaehlerStand
}

export function useHandregalZaehlstand() {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'handregal', 'zaehlstand', scope.key],
    queryFn: async () => {
      // apiFetch would unwrap data and discard the sibling pass status.
      const response = await apiFetchFull<HandregalZaehlstandZeile[]>(
        `/warehouse/handregal/zaehlstand/${scope.query ? `?${scope.query}` : ''}`,
      ) as HandregalZaehlstandAntwort
      return { stand: response.stand, zeilen: new Map((response.data ?? []).map(row => [row.merchant_sku, row])) }
    },
  })
}

export type HandregalKorrekturAntwort = {
  blocked: true
  code: string
  message: string
  qty_aktuell?: number
} | {
  blocked?: false
  merchant_sku: string
  qty_vorher: number
  qty: number
  delta: number
  duplicate: boolean
}

export interface HandregalVerlaufEintrag {
  id: number
  art: 'zaehlung' | 'korrektur' | 'inventur'
  qty_vorher: number | null
  qty_nachher: number | null
  wer: string | null
  geraet: string | null
  grund?: string | null
  at: string
}

export function useHandregalKorrektur() {
  const qc = useQueryClient()
  const scope = useWarehouseCompanyScope()
  return useMutation({
    mutationFn: ({ merchantSku, delta, qtyErwartet, idempotencyKey, reason }: KorrekturAuftrag) =>
      apiFetch<HandregalKorrekturAntwort>(`/warehouse/handregal/korrektur/${scope.query ? `?${scope.query}` : ''}`, {
        method: 'POST', body: JSON.stringify({ merchant_sku: merchantSku, delta, qty_erwartet: qtyErwartet, idempotency_key: idempotencyKey, reason }),
      }),
    retry: false,
    onSettled: () => qc.invalidateQueries({ queryKey: ['warehouse', 'handregal'] }),
  })
}

export function useHandregalVerlauf(merchantSku: string, enabled: boolean) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'handregal', 'verlauf', merchantSku, scope.key],
    queryFn: () => apiFetch<HandregalVerlaufEintrag[]>(
      `/warehouse/handregal/verlauf/${encodeURIComponent(merchantSku).replace(/\*/g, '%2A')}/${scope.query ? `?${scope.query}` : ''}`,
    ),
    enabled,
  })
}

export function useSetHandregalNachschub() {
  const qc = useQueryClient()
  const scope = useWarehouseCompanyScope()
  return useMutation({
    mutationFn: ({ merchantSku, menge_stueck, menge_kartons }: HandregalNachschubEingabe) =>
      apiFetch<HandregalNachschubAntwort>(`/warehouse/handregal/nachschub/${encodeURIComponent(merchantSku).replace(/\*/g, '%2A')}/`, {
        method: 'PUT', body: JSON.stringify({ menge_stueck, menge_kartons }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warehouse', 'handregal', 'nachschub', scope.key], exact: true }),
  })
}

export function useHandregalBestandAlle() {
  const scope = useWarehouseCompanyScope()
  return useQuery<BestandZeile[]>({
    queryKey: ['warehouse', 'handregal', 'alle', scope.key],
    queryFn: async (): Promise<BestandZeile[]> => {
      const alle: BestandZeile[] = []
      let after: string | null = null
      for (let seite = 0; seite < HANDREGAL_MAX_SEITEN; seite++) {
        const pfad: string = `/warehouse/handregal/alle/?limit=${HANDREGAL_SEITE}`
          + (after ? `&after=${encodeURIComponent(after)}` : '')
        // apiFetchFull, nicht apiFetch: bei gesetztem `data` packt apiFetch nur das
        // Array aus — der Cursor `next_after` liegt daneben im Envelope und ginge verloren.
        const antwort = (await apiFetchFull<BestandZeile[]>(pfad)) as
          AckResponse<BestandZeile[]> & { next_after?: string | null }
        alle.push(...(antwort.data ?? []))
        if (!antwort.next_after) return alle
        after = antwort.next_after
      }
      throw new Error(`Handregal-Bestand: mehr als ${HANDREGAL_MAX_SEITEN} Seiten — Abbruch statt Endlosschleife`)
    },
  })
}

export interface PalletTransportRecord {
  source_lager?: 1 | 2
  target_lager?: 1 | 2
  transport_id: number
  version: number
  generation: number
  status: 'ready' | 'in_transit' | 'placed' | 'cancelled'
  merchant_sku: string
  palette_no: string
  quantity: number | null
  unit: string
  company: WarehouseCompany
  owner_key?: string | null
  device_name?: string | null
  can_cancel_ready?: boolean
  worker_id?: number | null
  roll_length_m?: number | null
  place_barcode?: string | null
  return_required?: boolean
}
export interface PalletOverview {
  lists: { fuss: PalletTransportRecord[]; stapler: PalletTransportRecord[] }
  vormerkungen: Array<{ id: number; merchant_sku: string; roll_length_m: number | null; source_lager?: 1 | 2 | null; target_lager?: 1 | 2 }>
  progress: { offen: number }
}
export function usePalletCapabilities() {
  const { user } = useAuth()
  return useQuery({
    queryKey: ['warehouse', 'pallet', 'capability', user?.id],
    queryFn: async () => {
      try {
        const r = await apiFetch<{ capability?: string; enabled?: boolean; replace_contract?: string; cross_company_replace_enabled?: boolean }>(
          '/warehouse/fillup/pallet/capabilities/',
        )
        return { available: r.capability === 'pallet-transport-v1', enabled: r.enabled === true,
          crossCompanyReplaceEnabled: r.replace_contract === 'cross-company-v1' && r.cross_company_replace_enabled === true }
      } catch (error) {
        if (error instanceof ApiError && error.status === 404)
          return { available: false, enabled: false }
        throw error
      }
    },
    retry: false,
  })
}
export function usePalletOverview(company: WarehouseCompanyFilter, lager: 1 | 2 = 2, enabled = true) {
  const scope = useWarehouseCompanyScope(company)
  return useQuery({
    queryKey: ['warehouse', 'pallet', 'overview', scope.key, lager],
    enabled,
    retry: false,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    queryFn: () =>
      apiFetch<PalletOverview>(
        `/warehouse/fillup/overview/?request_kind=pallet&lager=${lager}${scope.query ? '&' + scope.query : ''}`,
      ),
  })
}
export function useCurrentPallet(company: WarehouseCompanyFilter) {
  const scope = useWarehouseCompanyScope(company)
  return useQuery({
    queryKey: ['warehouse', 'pallet', 'current', scope.key],
    retry: false,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    queryFn: () =>
      apiFetchFull<PalletTransportRecord | null>(
        `/warehouse/fillup/pallet/current/${scope.query ? '?' + scope.query : ''}`,
      ),
  })
}
export function palletApiPath(path: string, company: WarehouseCompanyFilter) {
  return company === 'both' ? path : `${path}${path.includes('?') ? '&' : '?'}company=${company}`
}

export function usePalletRequestPreview(
  code: string,
  company: WarehouseCompanyFilter,
  rollLength?: number,
) {
  const scope = useWarehouseCompanyScope(company)
  return useQuery({
    queryKey: ['warehouse', 'pallet', 'preview', code, rollLength, scope.key],
    enabled: code.trim().length >= 2,
    queryFn: () =>
      apiFetch<{
        source_lager: 1 | 2 | null
        target_lager: 1 | 2
        merchant_sku: string
        artikel_name: string
        roll_length_m: number | null
        has_stock: boolean
        quantity: number | null
      }>(
        palletApiPath(
          `/warehouse/fillup/request/preview/?request_kind=pallet&source_lager=2&code=${encodeURIComponent(code.trim())}${rollLength === undefined ? '' : '&roll_length_m=' + rollLength}`,
          scope.company,
        ),
      ),
  })
}

// Leere Plätze prüfen — Leiter-Vertrag aus routes.ts / leerplatz.ts (0160).
// packplatz_app_aktiv optional: Backend vor 0165 liefert das Feld nicht; nullmengen_aktiv erst ab 0172.
export interface WarehouseModuleSettings { erstaufnahme_sichtbar: boolean; packplatz_app_aktiv?: boolean; nullmengen_aktiv?: boolean }

// NULL-MENGEN (Plan 2026-10-27, 0172): Leiter-Liste. DR-Menge nur Anzeige, nie Schreibpfad.
export type NullmengenPackort = 'packstation' | 'packstation_ungezaehlt' | 'packplatz' | 'packplatz_ungezaehlt' | 'kein'
export interface NullmengenFall {
  id: number; merchant_sku: string; name: string | null; packort: NullmengenPackort
  phase: 'stufe1' | 'stufe2' | 'leer_warten' | 'nachlauf' | 'geschlossen'; runde: number
  faellig_ab: string | null; zaehlbar: boolean; frist_ueberschritten: boolean; s1: number; s2: number
  auftrag_nr: number; bestand: number | null; dr_menge: number | null; geoeffnet_at: string
  geschlossen_at: string | null; schliess_grund: 'nachlauf_fertig' | 'aufgefuellt' | 'grosslager_belegt' | 'ausgeschlossen' | null; packplatz: string | null; klaerfall?: 'packort_doppelt' | null
  letzte_zaehlung: { menge: number; gezaehlt_at: string; worker_name: string | null } | null
}
// Leiter-Übersicht NULL-MENGEN (nur Lesen): Statistik, Filter, Sortierung, Verlauf.
export type NullmengenPhase = NullmengenFall['phase']
export type NullmengenZaehlPhase = 'stufe1' | 'stufe2' | 'nachlauf'
/** nicht_gezaehlt = jetzt fällig; gezaehlt = zuletzt gezählt, nichts zu tun; wartet = noch nie gezählt, nicht fällig. */
export type NullmengenZaehlstand = 'nicht_gezaehlt' | 'gezaehlt' | 'wartet' | 'klaerfall' | 'geschlossen'
export interface NullmengenZeile extends NullmengenFall {
  letzte_zaehlung: { menge: number; gezaehlt_at: string; worker_name: string | null; worker_id: number | null
    device_label: string | null; phase: NullmengenZaehlPhase; runde: number; ergebnis_phase: NullmengenPhase } | null
  dr_sku: string | null; ordner: string | null; geloescht: boolean; geaendert_at: string; frist_bis: string | null
  start_faellig_ab: string; start_grund: 'bestand_unter_s1' | 'bestand_unter_s2' | 'ohne_bestand'; frist_gemeldet_at: string | null
  anzahl_zaehlungen: number; zaehlstand: NullmengenZaehlstand
}
export interface NullmengenMitarbeiter { worker_id: number | null; worker_name: string | null; anzahl: number; heute: number; letzte_at: string }
export interface NullmengenStatistik {
  offen: number; faellig: number; ueberfaellig: number; nicht_faellig: number; klaerfall: number; geloescht: number
  offen_gezaehlt: number; offen_nie_gezaehlt: number; ohne_termin: number
  zaehlungen: number; faelle_gezaehlt: number; heute_gezaehlt: { zaehlungen: number; faelle: number }
  geschlossen: { gesamt: number; nachlauf_fertig: number; aufgefuellt: number; grosslager_belegt: number; ausgeschlossen: number }
  phasen: { phase: NullmengenPhase; runde: number; anzahl: number }[]
  packorte: { packort: NullmengenPackort; anzahl: number }[]
  naechste_tage: { tag: string; anzahl: number }[]; spaeter: number; naechste_faelligkeit: string | null
  mitarbeiter: NullmengenMitarbeiter[]
}
export interface NullmengenUebersicht {
  statistik: NullmengenStatistik; faelle: NullmengenZeile[]; gesamt: number; tage: number; jetzt: string
  mitarbeiter: NullmengenMitarbeiter[]
}
export interface NullmengenJournalEintrag {
  id: number; fall_id: number; merchant_sku: string; name: string | null; dr_sku: string | null; auftrag_nr: number
  gezaehlt_at: string; menge: number; worker_id: number | null; worker_name: string | null; device_label: string | null
  phase: NullmengenZaehlPhase; runde: number; packort: NullmengenPackort; fundort: 'packstation' | 'packplatz' | null
  ergebnis_phase: NullmengenPhase; ergebnis_runde: number
}
export interface NullmengenJournal {
  eintraege: NullmengenJournalEintrag[]; gesamt: number; abgeschnitten: boolean; von: string; mitarbeiter: NullmengenMitarbeiter[]
  next_cursor: { before_at: string; before_id: number } | null
}
const nullmengenQuery = (f: Record<string, string>) => {
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v !== '')).toString()
  return qs ? `?${qs}` : ''
}
export function useNullmengenUebersicht(f: Record<string, string>) {
  const { warehouse } = useAuth()
  return useQuery({ queryKey: ['warehouse', 'nullmengen', 'uebersicht', f],
    queryFn: () => apiFetch<NullmengenUebersicht>(`/warehouse/nullmengen/uebersicht/${nullmengenQuery(f)}`),
    // Beim Filtern bleibt die alte Tabelle stehen, bis die neue da ist.
    placeholderData: prev => prev, enabled: warehouse.lead, retry: false })
}
export function useNullmengenJournal(f: Record<string, string>, enabled = true) {
  const { warehouse } = useAuth()
  return useQuery({ queryKey: ['warehouse', 'nullmengen', 'journal', f],
    queryFn: () => apiFetch<NullmengenJournal>(`/warehouse/nullmengen/journal/${nullmengenQuery(f)}`),
    placeholderData: prev => prev, enabled: warehouse.lead && enabled, retry: false })
}
export interface LeerplatzRun {
  id: number; title: string; status: 'offen' | 'abgeschlossen'
  started_by_user_id: number | null; started_by_name: string; started_at: string; start_move_id: number
  closed_by_user_id: number | null; closed_by_name: string | null; closed_at: string | null; close_move_id: number | null
  close_forced: boolean; soll_saeulen: number | null; gepruefte_saeulen: number | null; offene_abweichungen: number | null
}
export interface LeerplatzRunDetail extends LeerplatzRun {
  saeulen: Array<{ row_code: string; field_no: number; slot_no: number; levels: number[] }>
  fortschritt: Array<{ row_code: string; soll: number; geprueft: number; offen: string[] }>
}
export interface LeerplatzCell {
  place_code: string; field_no: number; slot_no: number; level: number
  system: { position_id: number; p_nr: string | null; sternnummer: string | null } | null
  pruefung: 'ungeprueft' | 'abweichung' | 'bestaetigt'; offene_warnungen: number
}
export interface LeerplatzAbweichung {
  id: number; place_code: string; kind: 'vergessene_palette' | 'geisterpalette' | 'notiz_leer'; position_id: number | null
  event_id: number; status: string; resolved_at: string | null; resolved_note: string | null
  check_id: number; worker_name: string; checked_at: string
}
export interface LeerplatzJournalEntry {
  check_id: number; checked_at: string; worker_id: number; worker_name: string; device_label: string | null
  column: string; leer_levels: number[]; abweichungen: number
}
export interface LeerplatzCloseResult {
  run_id: number; soll_saeulen: number; gepruefte_saeulen: number; offene_abweichungen: number; close_forced: boolean
}
export interface LeerplatzListFilter { limit: number; offset: number; worker_id?: number; row_code?: string }
const moduleSettingsKey = ['warehouse', 'module-settings'] as const
export function useModuleSettings() {
  const { warehouse, user } = useAuth()
  return useQuery({ queryKey: [...moduleSettingsKey, user?.id, warehouse.general],
    queryFn: () => apiFetch<WarehouseModuleSettings>('/warehouse/leerplatz/module-settings/'),
    enabled: warehouse.general, retry: false })
}
export function useSaveModuleSettings() {
  const qc = useQueryClient()
  return useMutation({
    // Nur den geänderten Schalter senden: der Server schreibt nur gesendete Felder.
    mutationFn: (change: Partial<WarehouseModuleSettings>) => apiFetch<WarehouseModuleSettings>('/warehouse/leerplatz/module-settings/',
      { method: 'PUT', body: JSON.stringify(change) }),
    onSuccess: settings => { qc.setQueriesData({ queryKey: moduleSettingsKey }, settings) },
  })
}
export function startLeerplatzRun(body: { title: string; row_codes: string[] }) {
  return apiFetch<{ run_id: number; soll_saeulen: number }>('/warehouse/leerplatz/runs/', { method: 'POST', body: JSON.stringify(body) })
}
export function closeLeerplatzRun(id: number, force = false) {
  return apiFetch<LeerplatzCloseResult>(`/warehouse/leerplatz/runs/${id}/close/`, { method: 'POST', body: JSON.stringify({ force }) })
}
export function useLeerplatzRuns() {
  const { user, warehouse } = useAuth()
  return useQuery({ queryKey: ['warehouse', 'leerplatz', 'runs', user?.id, warehouse.lead],
    queryFn: () => apiFetch<LeerplatzRun[]>('/warehouse/leerplatz/runs/'), enabled: warehouse.lead, refetchInterval: 30_000, retry: false })
}
export function useLeerplatzRun(id: number | undefined) {
  const { user, warehouse } = useAuth()
  return useQuery({ queryKey: ['warehouse', 'leerplatz', id, 'detail', user?.id, warehouse.lead],
    queryFn: () => apiFetch<LeerplatzRunDetail>(`/warehouse/leerplatz/runs/${id}/`), enabled: warehouse.lead && id !== undefined, refetchInterval: 30_000, retry: false })
}
export function useLeerplatzRegal(id: number | undefined, row: string) {
  const { user, warehouse } = useAuth()
  return useQuery({ queryKey: ['warehouse', 'leerplatz', id, 'regal', row, user?.id, warehouse.lead],
    queryFn: () => apiFetch<LeerplatzCell[]>(`/warehouse/leerplatz/runs/${id}/regal/${encodeURIComponent(row)}/`),
    enabled: warehouse.lead && id !== undefined && !!row, refetchInterval: 30_000, retry: false })
}
async function leerplatzList<T>(id: number, kind: string, filter: LeerplatzListFilter) {
  const qs = new URLSearchParams({ limit: String(filter.limit), offset: String(filter.offset) })
  if (filter.worker_id !== undefined) qs.set('worker_id', String(filter.worker_id))
  if (filter.row_code) qs.set('row_code', filter.row_code)
  // total steht neben data: apiFetch würde den Gesamtzähler verwerfen.
  const response = await apiFetchFull<T[]>(`/warehouse/leerplatz/runs/${id}/${kind}/?${qs}`) as AckResponse<T[]> & { total: number }
  return { data: response.data ?? [], total: response.total }
}
export function useLeerplatzAbweichungen(id: number | undefined, filter: LeerplatzListFilter) {
  const { user, warehouse } = useAuth()
  return useQuery({ queryKey: ['warehouse', 'leerplatz', id, 'abweichungen', filter, user?.id, warehouse.lead],
    queryFn: () => leerplatzList<LeerplatzAbweichung>(id!, 'abweichungen', filter), enabled: warehouse.lead && id !== undefined, refetchInterval: 30_000, retry: false })
}
export function useLeerplatzJournal(id: number | undefined, filter: LeerplatzListFilter, enabled = true) {
  const { user, warehouse } = useAuth()
  return useQuery({ queryKey: ['warehouse', 'leerplatz', id, 'journal', filter, user?.id, warehouse.lead],
    queryFn: () => leerplatzList<LeerplatzJournalEntry>(id!, 'journal', filter), enabled: warehouse.lead && enabled && id !== undefined, refetchInterval: 30_000, retry: false })
}

// ---------- Lager-Navi (Plan 2026-10-23 S3): nur lesend ----------
export type NaviStatus = 'frei' | 'belegt' | 'wartet' | 'notiz'
export interface NaviPosition {
  label_id: number | null
  palette_no: string | null; merchant_sku: string | null; name: string | null
  quantity: number | null; circa: boolean; unit: 'm' | 'St.'; first_in: string | null
}
export interface NaviZelle {
  place_id: number; barcode: string; field_no: number; slot_no: number; level: number; zone: string
  status: NaviStatus; packplatz: boolean; positionen: NaviPosition[]
  /** Paletten ausserhalb des eigenen Firmen-Leseumfangs: belegen den Platz, ohne Details. */
  verdeckt: number
  notiz: { text: string; von: string; seit: string } | null
}
export interface NaviZaehler { plaetze: number; frei: number; belegt: number; wartet: number; notiz: number; packplatz: number }
export interface NaviRegal { reihe: string; zaehler: NaviZaehler; zellen: NaviZelle[] }
export function useNaviRegal(reihe: string) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'navi', 'regal', reihe, scope.key],
    queryFn: () => apiFetch<NaviRegal>(`/warehouse/navi/regal/?reihe=${encodeURIComponent(reihe)}`),
    enabled: !!reihe, staleTime: 30_000, retry: false,
  })
}
export interface NaviArtikelTreffer { stock_item_id: number; sku: string; merchant_sku: string | null; name: string }
export type NaviSuchErgebnis = { q: string } & (
  | { art: 'palette'; palette: (NaviPosition & { state: ArtikelPosition['state']; barcode: string | null; reihe: string | null }) | null }
  | { art: 'platz'; platz: { barcode: string; reihe: string; aktiv: boolean } | null }
  | { art: 'reihe'; reihe: string; plaetze: number }
  | { art: 'artikel'; quelle: 'sternnummer' | 'name'; artikel: NaviArtikelTreffer[] })
export function useNaviSuche(q: string) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'navi', 'suche', q, scope.key],
    queryFn: () => apiFetch<NaviSuchErgebnis>(`/warehouse/navi/suche/?q=${encodeURIComponent(q)}`),
    enabled: !!q, staleTime: 30_000, retry: false,
  })
}
export interface NaviReihenWert { reihe: string; lager: 1 | 2; zone: string; plaetze: number; frei: number; belegt: number; wartet: number; notiz: number; frei_ebene1: number }
export interface NaviUebersicht {
  kpi: { plaetze: number; frei: number; belegt: number; wartet: number; notiz: number; frei_ebene1: number; belegung_prozent: number }
  reihen: NaviReihenWert[]
}
export function useNaviUebersicht(enabled = true) {
  const scope = useWarehouseCompanyScope()
  return useQuery({
    queryKey: ['warehouse', 'navi', 'uebersicht', scope.key],
    queryFn: () => apiFetch<NaviUebersicht>('/warehouse/navi/uebersicht/'),
    enabled, staleTime: 60_000, retry: false,
  })
}
